// The `<section>` wrapper. The full pane anchors to the pane edges; the overlay
// (inPanel) fills its host box instead.
export function sectionStyle(inPanel: boolean, hasConversation: boolean): React.CSSProperties {
  return {
    position: inPanel ? 'relative' : 'absolute',
    // The overlay fills the FloatingChat panel; the full pane anchors to the
    // pane edges (the inline-start offset lives on .orion-chat-pane — default
    // 280px, 0 below the narrow breakpoint via @media — so the inline
    // transition animates the slide). insetInlineEnd:0 so the pane mirrors in RTL.
    ...(inPanel ? { height: '100%', width: '100%' } : { top: 0, insetInlineEnd: 0, bottom: 0 }),
    zIndex: inPanel ? 1 : 4,
    // Faint atmospheric tints — primary accent (sconce-light on Symphony)
    // and a translucent base so the backdrop behind reads through as
    // ambient depth. The overlay panel supplies its own surface, so it
    // stays transparent here.
    background: inPanel
      ? 'transparent'
      : 'radial-gradient(ellipse 70% 60% at 28% 12%, rgba(var(--accent-rgb),0.07) 0%, transparent 60%),' +
        'rgba(var(--surface-base-rgb), 0.55)',
    // No blur on the empty state (constellation stays crisp), small blur
    // once a conversation is active so pinpoint stars don't compete with
    // the prose. The overlay panel has no constellation behind it, so no
    // blur there.
    backdropFilter: inPanel ? 'none' : hasConversation ? 'blur(5px)' : 'none',
    WebkitBackdropFilter: inPanel ? 'none' : hasConversation ? 'blur(5px)' : 'none',
    // The inline-start offset is transitioned so the full pane slides in sync
    // with the sidebar drawer on viewport-resize; the overlay panel doesn't move.
    transition: inPanel ? 'none' : 'backdrop-filter 800ms ease, inset-inline-start 260ms ease',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: undefined,
  };
}

// The `<form>` composer wrapper. `hidden` collapses it to `display: none` (the
// missing-movement empty state).
export function formStyle({
  compact,
  narrow,
  sideDocked,
  hidden,
}: {
  compact: boolean;
  narrow: boolean;
  sideDocked: boolean;
  hidden: boolean;
}): React.CSSProperties {
  return {
    position: 'relative',
    // Same padding for empty landing and active thread so the composer is
    // pinned at the exact same spot in both (only the small panel differs).
    // Side dock (370px column): 20px gutters so the composer lands at the
    // fixed 330px width the spec calls for.
    // Narrow (full-screen mobile chat) sits flush to the viewport bottom —
    // add the home-indicator safe area so the composer doesn't render under it.
    // Fullscreen: NO horizontal gutter, so the pill fills the full 720 reading
    // column (Figma "Prompt box" 14701:126613 — 720×118) and lines up edge-to-edge
    // with the 720 transcript column above it. A gutter here would shrink the pill
    // below 720 (48px each side used to make it 624).
    padding: compact
      ? '10px 14px 14px'
      : narrow
        ? '14px 14px calc(18px + env(safe-area-inset-bottom))'
        : sideDocked
          ? '18px 20px 24px'
          : '18px 0 24px',
    display: hidden ? 'none' : 'flex',
    flexDirection: 'column',
    gap: 8,
    // Constrain to a comfortable centered reading column (matching the
    // Figma) so the composer + conversation aren't stretched across the
    // whole pane. The compact floating panel keeps its full width; the side
    // dock fills its 370px column so the composer is exactly 330px wide.
    maxWidth: compact ? undefined : sideDocked ? 'none' : 720,
    width: compact ? undefined : '100%',
    alignSelf: compact ? undefined : 'center',
    boxSizing: 'border-box',
    // NOTE: do not set `filter` here (or any other property that
    // establishes a containing block). VoiceCallOverlay is
    // `position: fixed; inset: 0` and must escape to the viewport;
    // filter/transform/contain on any ancestor would clip it inside
    // the form's bounds and make the call surface render on top of
    // the empty-landing input + 4U instead of replacing the screen.
  };
}

export function scrollAreaStyle(compact: boolean, narrow: boolean, inPanel: boolean, sideDocked: boolean): React.CSSProperties {
  // Soft-fade the top/bottom edges so messages dissolve into the header and
  // composer instead of hard-cutting mid-line as they scroll under them.
  // `mask` uses alpha only, so the token here is just opaque-vs-transparent —
  // it never paints a colour (safe for both brands / dark mode).
  const fadeTop = compact ? 12 : 20;
  const fadeBottom = compact ? 18 : 28;
  const edgeFade = `linear-gradient(to bottom, transparent 0, rgba(var(--ds-black-rgb),1) ${fadeTop}px, rgba(var(--ds-black-rgb),1) calc(100% - ${fadeBottom}px), transparent 100%)`;
  return {
    flex: 1,
    // Side dock (370px column): Figma chat spec — 28px left · 20px right gutters
    // (top | right | bottom | left).
    padding: compact
      ? '14px 16px 12px'
      : narrow
        ? inPanel
          ? '20px 16px 16px'
          : '64px 16px 16px'
        : sideDocked
          ? '24px 20px 24px 28px'
          : '32px 48px 24px',
    position: 'relative',
    zIndex: 2,
    maskImage: edgeFade,
    WebkitMaskImage: edgeFade,
  };
}

// The centered reading column inside the scroll area. Full-width in the compact panel.
export function contentColumnStyle(compact: boolean): React.CSSProperties | undefined {
  return compact ? undefined : { maxWidth: 720, marginInline: 'auto', width: '100%' };
}
